/**
 * Shared helpers for filtering federal bills by member (sponsor/cosponsor).
 * Used when navigating from dashboard rep cards, Congress member profiles, etc.
 */

export function isValidBioguideId(id) {
  if (!id || typeof id !== "string") return false;
  const normalized = id.trim();
  if (!normalized || normalized === "N/A" || normalized === "NOT FOUND") return false;
  return true;
}

/** Dashboard MemberCard roles map to chamber for bills filter placement. */
export function memberPositionFromDashboardRole(role) {
  if (/senator/i.test(role || "")) return "senate";
  return "house";
}

/** Bills sponsored or cosponsored — matches "View Supported Legislation" intent. */
export function supportedLegislationMember(bioguideId, name) {
  return {
    bioguideId,
    name: (name && String(name).trim()) || bioguideId,
    sponsored: true,
    cosponsored: true,
  };
}

function mergeMemberLists(arr) {
  const byId = {};
  arr.forEach((m) => {
    if (!byId[m.bioguideId]) {
      byId[m.bioguideId] = { ...m };
      return;
    }
    byId[m.bioguideId].sponsored = byId[m.bioguideId].sponsored || m.sponsored;
    byId[m.bioguideId].cosponsored = byId[m.bioguideId].cosponsored || m.cosponsored;
  });
  return Object.values(byId).filter((m) => m.sponsored || m.cosponsored);
}

/**
 * Legacy activeFilter tokens from older navigation (dashboard, previews).
 * @param {string[]} activeFilter
 * @param {object} user
 */
export function membersFromLegacyActiveFilter(activeFilter, user = {}) {
  const senators = [];
  const reps = [];
  const filters = new Set(activeFilter || []);

  if (filters.has("sponsored_rep") || filters.has("cosponsored_rep")) {
    if (isValidBioguideId(user.userRepsBioguideID)) {
      reps.push(supportedLegislationMember(user.userRepsBioguideID, user.userRepsName));
    }
  }
  if (filters.has("sponsored_sen_one") || filters.has("cosponsored_sen_one")) {
    if (isValidBioguideId(user.userSenOneBioguideID)) {
      senators.push(supportedLegislationMember(user.userSenOneBioguideID, user.userSenOneName));
    }
  }
  if (filters.has("sponsored_sen_two") || filters.has("cosponsored_sen_two")) {
    if (isValidBioguideId(user.userSenTwoBioguideID)) {
      senators.push(supportedLegislationMember(user.userSenTwoBioguideID, user.userSenTwoName));
    }
  }

  return {
    senators: mergeMemberLists(senators),
    representatives: mergeMemberLists(reps),
  };
}

/**
 * Resolve senator/representative filter rows from React Router location.state.
 * @returns {{ senators: object[], representatives: object[] } | null}
 */
export function membersFromNavState(navState, user = {}) {
  if (!navState) return null;

  const bioguideId = navState.memberBioguideId;
  if (isValidBioguideId(bioguideId)) {
    const member = supportedLegislationMember(bioguideId, navState.memberName);
    const isSenate = String(navState.memberPosition || "").toLowerCase() === "senate";
    return {
      senators: isSenate ? [member] : [],
      representatives: isSenate ? [] : [member],
    };
  }

  if (navState.activeFilter?.length) {
    const legacy = membersFromLegacyActiveFilter(navState.activeFilter, user);
    if (legacy.senators.length || legacy.representatives.length) return legacy;
  }

  return null;
}

export function hasMemberNavState(navState) {
  if (!navState) return false;
  if (isValidBioguideId(navState.memberBioguideId)) return true;
  if (navState.activeFilter?.length) return true;
  return false;
}
